import React, { useState, useEffect } from "react";
import Header from "../Components/Header";
import {
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import db from "../firestore";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Reusable inline edit field — shows current value as placeholder, saves on Update click
function EditField({ label, value, docRef, fieldKey, multiline }) {
  const [draft, setDraft] = useState("");

  const handleUpdate = async () => {
    if (!draft) return;
    await updateDoc(docRef, { [fieldKey]: draft });
    toast.success("Updated 👍", { position: "top-center", autoClose: 3000 });
    setDraft("");
  };

  return (
    <div className="flex flex-col">
      <h2 className="font-bold text-lg">{label}</h2>
      {multiline ? (
        <textarea
          placeholder={value || ""}
          value={draft}
          className="border p-2"
          rows={3}
          onChange={(e) => setDraft(e.target.value)}
        />
      ) : (
        <input
          type="text"
          placeholder={value || ""}
          value={draft}
          className="border h-10 p-2"
          onChange={(e) => setDraft(e.target.value)}
        />
      )}
      <button
        className="bg-add-green text-white w-32 h-10 rounded mt-3 disabled:opacity-40"
        disabled={!draft}
        onClick={handleUpdate}
      >
        Update
      </button>
    </div>
  );
}

// Page sections config — defines which fields each Firestore document exposes
const PAGE_CONFIGS = [
  {
    title: "Edit Home Page",
    id: "Home",
    docId: "Home",
    fields: [
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
      { label: "Clothing 1 H1", key: "Clothing1H1" },
      { label: "Clothing 1 H2", key: "Clothing1H2", multiline: true },
      { label: "Clothing 1 Name", key: "Clothing1Name" },
      { label: "OEM/ODM H1", key: "OEMH1" },
      { label: "OEM/ODM H2", key: "OEMH2", multiline: true },
      { label: "Clothing 2 H1", key: "Clothing2H1" },
      { label: "Clothing 2 H2", key: "Clothing2H2", multiline: true },
      { label: "Clothing 2 Name", key: "Clothing2Name" },
    ],
  },
  {
    title: "Edit About Page",
    id: "About",
    docId: "About",
    fields: [
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
      { label: "Content H1", key: "ContentH1" },
      { label: "Content H2", key: "ContentH2", multiline: true },
    ],
  },
  {
    title: "Edit OEM/ODM Page",
    id: "OEM-ODM",
    docId: "OEM-ODM",
    fields: [
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
      { label: "Section 1 H1", key: "Clothing1H1" },
      { label: "Section 1 H2", key: "Clothing1H2", multiline: true },
      { label: "Section 2 H1", key: "Clothing2H1" },
      { label: "Section 2 H2", key: "Clothing2H2", multiline: true },
      { label: "Section 3 H1", key: "Clothing3H1" },
      { label: "Section 3 H2", key: "Clothing3H2", multiline: true },
    ],
  },
  {
    title: "Edit Organic Page",
    id: "Organic",
    docId: "Organic",
    fields: [
      { label: "Subtitle", key: "subTitle" },
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
      { label: "Section 1 H1", key: "Clothing1H1" },
      { label: "Section 1 H2", key: "Clothing1H2", multiline: true },
      { label: "Section 2 H1", key: "Clothing2H1" },
      { label: "Section 2 H2", key: "Clothing2H2", multiline: true },
      { label: "Section 3 H1", key: "Clothing3H1" },
      { label: "Section 3 H2", key: "Clothing3H2", multiline: true },
    ],
  },
  {
    title: "Edit Products Page",
    id: "Products",
    docId: "Products",
    fields: [
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
      { label: "End Text", key: "EndText", multiline: true },
    ],
  },
  {
    title: "Edit Men Wear Page",
    id: "MensWear",
    docId: "Men",
    fields: [
      { label: "Subtitle", key: "subTitle" },
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
    ],
  },
  {
    title: "Edit Women Wear Page",
    id: "WomenWear",
    docId: "Women",
    fields: [
      { label: "Subtitle", key: "subTitle" },
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
    ],
  },
  {
    title: "Edit Knit Wear Page",
    id: "Knit",
    docId: "Knit",
    fields: [
      { label: "Subtitle", key: "subTitle" },
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
    ],
  },
  {
    title: "Edit Kid Wear Page",
    id: "Kids",
    docId: "Kid",
    fields: [
      { label: "Subtitle", key: "subTitle" },
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
    ],
  },
  {
    title: "Edit Corporate Uniform Page",
    id: "Uniform",
    docId: "Uniform",
    fields: [
      { label: "Subtitle", key: "subTitle" },
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
    ],
  },
  {
    title: "Edit Accessories Page",
    id: "Accessories",
    docId: "Accessories",
    fields: [
      { label: "Subtitle", key: "subTitle" },
      { label: "H1", key: "H1" },
      { label: "H2", key: "H2", multiline: true },
    ],
  },
];

function Dashboard() {
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  // pageData maps docId -> { _ref: DocumentReference, ...fieldValues }
  const [pageData, setPageData] = useState({});

  useEffect(() => {
    getDocs(collection(db, "Testimonials")).then((snap) =>
      setTestimonials(snap.docs.map((d) => ({ ...d.data(), id: d.id })))
    );
    getDocs(collection(db, "Faq")).then((snap) =>
      setFaqs(snap.docs.map((d) => ({ ...d.data(), id: d.id })))
    );

    const docIds = [...new Set(PAGE_CONFIGS.map((p) => p.docId))];
    docIds.forEach((docId) => {
      const ref = doc(db, "Pages", docId);
      getDoc(ref).then((snap) => {
        setPageData((prev) => ({
          ...prev,
          [docId]: { _ref: ref, _data: snap.data() ?? {} },
        }));
      });
    });
  }, []);

  const deleteTestimonial = async (id) => {
    await deleteDoc(doc(db, "Testimonials", id));
    setTestimonials((prev) => prev.filter((t) => t.id !== id));
    toast.success("Deleted", { position: "top-center", autoClose: 3000 });
  };

  const deleteFaq = async (id) => {
    await deleteDoc(doc(db, "Faq", id));
    setFaqs((prev) => prev.filter((f) => f.id !== id));
    toast.success("Deleted", { position: "top-center", autoClose: 3000 });
  };

  const quickLinks = [
    { label: "Add Blog", to: "/dashboard/addBlog", isLink: true },
    { label: "Blogs", to: "/dashboard/manageBlogs", isLink: true },
    { label: "Certificates", to: "/dashboard/Certificate", isLink: true },
    { label: "Images", to: "/dashboard/images", isLink: true },
    { label: "Testimonials", href: "#Testimonials" },
    { label: "FAQs", href: "#FAQs" },
    ...PAGE_CONFIGS.map((p) => ({ label: p.title.replace("Edit ", ""), href: `#${p.id}` })),
  ];

  return (
    <div className="flex flex-col w-screen justify-center items-center">
      <Header />
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Quick Links */}
      <div className="w-screen flex flex-col px-20 py-10">
        <h1 className="text-xl mb-3">Quick Links</h1>
        <div className="flex gap-3 flex-wrap">
          {quickLinks.map((ql) =>
            ql.isLink ? (
              <Link key={ql.label} to={ql.to}>
                <button className="rounded bg-edit-blue text-white px-4 py-2 w-max">
                  {ql.label}
                </button>
              </Link>
            ) : (
              <a key={ql.label} href={ql.href}>
                <button className="rounded bg-edit-blue text-white px-4 py-2 w-max">
                  {ql.label}
                </button>
              </a>
            )
          )}
        </div>
      </div>

      {/* Testimonials */}
      <div className="w-3/4 border rounded p-5 mt-5" id="Testimonials">
        <div className="flex w-full justify-between items-center pb-5">
          <h1 className="font-bold text-2xl">Testimonials</h1>
          <Link to="/dashboard/addTestimonial">
            <button className="rounded bg-add-green px-3 py-1 text-white text-2xl hover:bg-add-green2">
              +
            </button>
          </Link>
        </div>
        {testimonials.map((post) => (
          <div className="border-2 p-5 rounded mb-5" key={post.id}>
            <h2 className="text-xl font-bold">{post.Name}</h2>
            <p className="font-medium">{post.Title}</p>
            <p>{post.Text}</p>
            <div className="flex justify-center pt-5">
              <button
                className="rounded bg-delete-red px-5 py-2 text-white hover:bg-delete-red2"
                onClick={() => deleteTestimonial(post.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* FAQs */}
      <div className="w-3/4 border rounded p-5 mt-5" id="FAQs">
        <div className="flex w-full justify-between items-center pb-5">
          <h1 className="font-bold text-2xl">FAQs</h1>
          <Link to="/dashboard/addFAQ">
            <button className="rounded bg-add-green px-3 py-1 text-white text-2xl hover:bg-add-green2">
              +
            </button>
          </Link>
        </div>
        {faqs.map((post) => (
          <div className="border rounded p-5 mb-5" key={post.id}>
            <h2 className="text-lg font-bold">{post.Q}</h2>
            <p>{post.A}</p>
            <div className="flex justify-center pt-5">
              <button
                className="rounded bg-delete-red px-5 py-2 text-white hover:bg-delete-red2"
                onClick={() => deleteFaq(post.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Page content editors */}
      {PAGE_CONFIGS.map((config) => {
        const data = pageData[config.docId];
        if (!data) return null;
        return (
          <div className="w-3/4 border rounded p-5 mt-5" id={config.id} key={config.id}>
            <h1 className="font-bold text-2xl mb-8">{config.title}</h1>
            <div className="flex flex-col gap-8">
              {config.fields.map(({ label, key, multiline }) => (
                <EditField
                  key={key}
                  label={label}
                  value={data._data[key] ?? ""}
                  docRef={data._ref}
                  fieldKey={key}
                  multiline={multiline}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Dashboard;
